import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { playoffService } from '../services/playoffService';
import { useAdminAuth } from '../context/AdminAuthContext';
import { useSeasonContext } from '../context/SeasonContext';
import { seasonManagementService } from '../services/seasonManagementService';
import '../styles/PlayoffPicture.css';

const PlayoffPicture = () => {
  const { seasonId: paramSeasonId } = useParams();
  const { currentSeason } = useSeasonContext();
  const seasonId = paramSeasonId || currentSeason?.id;
  const { user, hasPermission } = useAdminAuth();
  const [playoffSettings, setPlayoffSettings] = useState(null);
  const [matchups, setMatchups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [season, setSeason] = useState(null);
  const isStaff = user && (hasPermission('manage_stats') || hasPermission('manage_permissions'));

  useEffect(() => {
    if (seasonId) {
      loadPlayoffData();
    } else {
      setLoading(false);
    }
  }, [seasonId]);

  const loadPlayoffData = async () => {
    try {
      setLoading(true);
      setError(null);

      const settings = await playoffService.getPlayoffSettings(seasonId);
      setPlayoffSettings(settings);

      if (!settings.playoffs_enabled && !isStaff) {
        setError('The playoff picture for this season is not currently available.');
        setLoading(false);
        return;
      }

      if (settings.id) {
        const matches = await playoffService.getMatchups(settings.id);
        setMatchups(matches || []);
      }

      setSeason({ id: seasonId });
    } catch (err) {
      setError(`Failed to load playoff data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!seasonId) {
    return (
      <div className="playoff-picture-page">
        <div className="unavailable-message">
          <h2>No Season Selected</h2>
          <p>Please select a season to view the playoff picture.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="playoff-picture-page">
        <div className="loading">Loading playoff data...</div>
      </div>
    );
  }

  if (!playoffSettings?.playoffs_enabled && !isStaff) {
    return (
      <div className="playoff-picture-page">
        <div className="unavailable-message">
          <h2>Playoff Picture Not Available</h2>
          <p>The playoff picture for this season is not currently available.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="playoff-picture-page">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  if (!playoffSettings) {
    return (
      <div className="playoff-picture-page">
        <div className="error-message">Failed to load playoff configuration</div>
      </div>
    );
  }

  const getRoundName = (roundNumber, totalRounds, formatType) => {
    if (formatType === 'double_elimination') {
      const winnersRounds = Math.ceil(Math.log2(playoffSettings.playoff_team_count));
      if (roundNumber <= winnersRounds) {
        if (roundNumber === 1) return 'Winners\' Round 1';
        if (roundNumber === winnersRounds) return 'Winners\' Finals';
        return `Winners\' Round ${roundNumber}`;
      } else {
        const loserRound = roundNumber - winnersRounds;
        if (loserRound === totalRounds - winnersRounds) return 'Grand Finals';
        return `Losers\' Round ${loserRound}`;
      }
    }

    const totalRound = Math.ceil(Math.log2(playoffSettings.playoff_team_count));
    if (totalRound === 1) return 'Finals';
    if (roundNumber === 1) return 'First Round';
    if (roundNumber === totalRound) return 'Finals';
    if (roundNumber === totalRound - 1) return 'Semifinals';
    if (roundNumber === totalRound - 2) return 'Conference Championship';
    return `Round ${roundNumber}`;
  };

  const roundsWithMatchups = matchups.length > 0
    ? Array.from({ length: Math.max(...matchups.map(m => m.round_number || 0)) }).map((_, i) => i + 1)
    : [];

  return (
    <div className="playoff-picture-page">
      <div className="playoff-header">
        <h1>Playoff Picture</h1>
        <p className="playoff-format">{playoffSettings.playoff_format_type.replace(/_/g, ' ').toUpperCase()}</p>
      </div>

      {matchups.length === 0 ? (
        <div className="no-bracket">
          <p>Playoff bracket not yet configured.</p>
        </div>
      ) : (
        <div className="playoff-bracket">
          {roundsWithMatchups.map((roundNumber) => {
            const roundMatchups = matchups.filter(m => m.round_number === roundNumber);
            const roundName = getRoundName(roundNumber, roundsWithMatchups.length, playoffSettings.playoff_format_type);

            return (
              <div key={roundNumber} className="bracket-round">
                <div className="round-header">
                  <h3>{roundName}</h3>
                </div>

                <div className="matchups-container">
                  {roundMatchups.map((matchup) => (
                    <div key={matchup.id} className="matchup">
                      <div className="matchup-content">
                        <div className={`team-row ${matchup.winner_id === matchup.team1_id ? 'winner' : ''}`}>
                          <div className="team-info">
                            {matchup.team1 ? (
                              <>
                                <div className="team-logo-container">
                                  {matchup.team1.logo_url && (
                                    <img src={matchup.team1.logo_url} alt={matchup.team1.name} className="team-logo" />
                                  )}
                                </div>
                                <div className="team-details">
                                  <span className="seed">({matchup.team1_seed})</span>
                                  <span className="team-name">{matchup.team1.name}</span>
                                </div>
                              </>
                            ) : (
                              <span className="tbd">TBD</span>
                            )}
                          </div>
                          {matchup.team1_score !== null && (
                            <div className="score">{matchup.team1_score}</div>
                          )}
                        </div>

                        <div className="matchup-divider">vs</div>

                        <div className={`team-row ${matchup.winner_id === matchup.team2_id ? 'winner' : ''}`}>
                          <div className="team-info">
                            {matchup.team2 ? (
                              <>
                                <div className="team-logo-container">
                                  {matchup.team2.logo_url && (
                                    <img src={matchup.team2.logo_url} alt={matchup.team2.name} className="team-logo" />
                                  )}
                                </div>
                                <div className="team-details">
                                  <span className="seed">({matchup.team2_seed})</span>
                                  <span className="team-name">{matchup.team2.name}</span>
                                </div>
                              </>
                            ) : (
                              <span className="tbd">TBD</span>
                            )}
                          </div>
                          {matchup.team2_score !== null && (
                            <div className="score">{matchup.team2_score}</div>
                          )}
                        </div>
                      </div>

                      {matchup.is_completed && matchup.winner && (
                        <div className="matchup-result">
                          <span className="winner-badge">{matchup.winner.abbreviation} WINS</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PlayoffPicture;
